import { $convertFromMarkdownString, $convertToMarkdownString } from '@lexical/markdown';
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { TableNode, TableRowNode, TableCellNode } from '@lexical/table';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { EditorState, $setSelection, HISTORY_MERGE_TAG } from 'lexical';
import { KeyboardArrowDown } from '@mui/icons-material';
import React, { useCallback, useEffect, useRef } from 'react';
import { MARKDOWN_TRANSFORMERS } from './markdownTransformers';
import { normalizeMarkdownOutput } from './normalizeMarkdown';
import classes from './LexicalMarkdownEditor.module.scss';
import { ToolbarPlugin } from './ToolbarPlugin';
import { FloatingTextFormatToolbarPlugin } from './FloatingTextFormatToolbarPlugin';
import { DraggableBlockPlugin } from './DraggableBlockPlugin';
import { SlashCommandPlugin } from './SlashCommandPlugin';
import { FloatingLinkEditorPlugin } from './FloatingLinkEditorPlugin';
import { TableActionMenuPlugin } from './TableActionMenuPlugin';
import { TableHoverActionsPlugin } from './TableHoverActionsPlugin';

const URL_MATCHER =
  /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;

const EMAIL_MATCHER =
  /(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/;

const AUTO_LINK_MATCHERS = [
  (text: string) => {
    const match = URL_MATCHER.exec(text);
    if (match === null) return null;
    const fullMatch = match[0];
    return {
      index: match.index,
      length: fullMatch.length,
      text: fullMatch,
      url: fullMatch.startsWith('http') ? fullMatch : `https://${fullMatch}`,
    };
  },
  (text: string) => {
    const match = EMAIL_MATCHER.exec(text);
    if (match === null) return null;
    const fullMatch = match[0];
    return {
      index: match.index,
      length: fullMatch.length,
      text: fullMatch,
      url: `mailto:${fullMatch}`,
    };
  },
];

export interface MarkdownEditorProps {
  placeholder?: string;
  committedValue: { value: string };
  onChange?: (markdown: string) => void;
  readOnly?: boolean;
  onFocusEditor?: () => void;
  onBlurEditor?: () => void;
  /**
   * Left side of the persistent bar shown in READ mode (e.g. a "DOCUMENTATION"
   * field-label). In edit mode the format controls take this slot instead.
   */
  barLabel?: React.ReactNode;
  /**
   * Right side of the persistent bar, shown in BOTH read and edit modes
   * (e.g. a "Generate" button). Stays in place as the left side swaps.
   */
  barAction?: React.ReactNode;
  /**
   * When true, the editor draws no outer border/background of its own — the
   * host container supplies the chrome (border, radius, bounded height). The
   * editor lays itself out as a flex column so its content scrolls internally
   * while the bar stays pinned at the top.
   */
  chromeless?: boolean;
  /** When available, adds a "Generate with AI" entry to the "/" slash menu. */
  aiSlash?: { available: boolean; onGenerate: () => void };
}

/**
 * Syncs committedValue into the editor when it changes externally.
 * Handles: initial load, late-arriving doc from API, read-only switch (Discard).
 */
function MarkdownSyncPlugin({
  committedValue,
  readOnly,
}: {
  committedValue: { value: string };
  readOnly: boolean;
}) {
  const [editor] = useLexicalComposerContext();
  const lastValueRef = useRef<string | undefined>(undefined);
  const isFirstRender = useRef(true);
  const prevReadOnlyRef = useRef(readOnly);

  useEffect(() => {
    const newValue = committedValue?.value ?? '';

    if (isFirstRender.current || lastValueRef.current !== newValue) {
      isFirstRender.current = false;
      lastValueRef.current = newValue;
      // HISTORY_MERGE_TAG so this programmatic load is NOT seen as a user edit:
      // OnChangePlugin ignores history-merge updates, so loading / switching /
      // late-arriving docs never fire onChange. Only real keystrokes do. This
      // keeps an untouched doc from being re-serialized (and thus from showing
      // a spurious "Unsaved changes" or rewriting stored Markdown on view).
      editor.update(
        () => {
          $convertFromMarkdownString(newValue, MARKDOWN_TRANSFORMERS, undefined, true);
        },
        { tag: HISTORY_MERGE_TAG },
      );
    }
  }, [committedValue, editor]);

  // When switching to read-only (e.g. Discard), restore saved content and clear
  // selection so the view doesn't auto-scroll to the old cursor position.
  useEffect(() => {
    if (!prevReadOnlyRef.current && readOnly) {
      const savedValue = committedValue?.value ?? '';
      lastValueRef.current = savedValue;
      editor.update(
        () => {
          $convertFromMarkdownString(savedValue, MARKDOWN_TRANSFORMERS, undefined, true);
          $setSelection(null);
        },
        { tag: HISTORY_MERGE_TAG },
      );
    }
    prevReadOnlyRef.current = readOnly;
  }, [readOnly, committedValue, editor]);

  return null;
}

function ReadOnlyPlugin({ readOnly }: { readOnly: boolean }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  return null;
}

const editorTheme = {
  code: 'editor-code',
  codeHighlight: {
    atrule: 'editor-tokenAttr',
    attr: 'editor-tokenAttr',
    boolean: 'editor-tokenProperty',
    builtin: 'editor-tokenSelector',
    cdata: 'editor-tokenComment',
    char: 'editor-tokenSelector',
    class: 'editor-tokenFunction',
    'class-name': 'editor-tokenFunction',
    comment: 'editor-tokenComment',
    constant: 'editor-tokenProperty',
    deleted: 'editor-tokenProperty',
    doctype: 'editor-tokenComment',
    entity: 'editor-tokenOperator',
    function: 'editor-tokenFunction',
    important: 'editor-tokenVariable',
    inserted: 'editor-tokenSelector',
    keyword: 'editor-tokenAttr',
    namespace: 'editor-tokenVariable',
    number: 'editor-tokenProperty',
    operator: 'editor-tokenOperator',
    prolog: 'editor-tokenComment',
    property: 'editor-tokenProperty',
    punctuation: 'editor-tokenPunctuation',
    regex: 'editor-tokenVariable',
    selector: 'editor-tokenSelector',
    string: 'editor-tokenSelector',
    symbol: 'editor-tokenProperty',
    tag: 'editor-tokenProperty',
    url: 'editor-tokenOperator',
    variable: 'editor-tokenVariable',
  },
  horizontalRule: 'editor-hr',
  heading: {
    h1: 'editor-heading-h1',
    h2: 'editor-heading-h2',
    h3: 'editor-heading-h3',
    h4: 'editor-heading-h4',
    h5: 'editor-heading-h5',
    h6: 'editor-heading-h6',
  },
  link: 'editor-link',
  list: {
    listitem: 'editor-listitem',
    listitemChecked: 'editor-listitem-checked',
    listitemUnchecked: 'editor-listitem-unchecked',
    nested: {
      listitem: 'editor-nested-listitem',
    },
    ol: 'editor-list-ol',
    ul: 'editor-list-ul',
    checklist: 'editor-checklist',
  },
  ltr: 'ltr',
  paragraph: 'editor-paragraph',
  quote: 'editor-quote',
  rtl: 'rtl',
  table: 'editor-table',
  tableCell: 'editor-table-cell',
  tableCellHeader: 'editor-table-cell-header',
  tableRow: 'editor-table-row',
  text: {
    bold: 'editor-text-bold',
    code: 'editor-text-code',
    italic: 'editor-text-italic',
    strikethrough: 'editor-text-strikethrough',
    subscript: 'editor-text-subscript',
    superscript: 'editor-text-superscript',
    underline: 'editor-text-underline',
    underlineStrikethrough: 'editor-text-underlineStrikethrough',
  },
};

const EDITOR_NODES = [
  HeadingNode,
  QuoteNode,
  ListNode,
  ListItemNode,
  CodeNode,
  CodeHighlightNode,
  LinkNode,
  AutoLinkNode,
  HorizontalRuleNode,
  TableNode,
  TableRowNode,
  TableCellNode,
];

/** Invisible plugins - no DOM output, just effects */
function BackgroundPlugins({
  committedValue,
  readOnly,
  onChange,
  onFocusEditor,
  onBlurEditor,
  placeholder,
  anchorElem,
  aiSlash,
}: MarkdownEditorProps & { anchorElem: HTMLElement | null }) {
  const handleChange = useCallback(
    (editorState: EditorState) => {
      if (!onChange) return;
      editorState.read(() => {
        // shouldPreserveNewLines=true so blank lines survive the round-trip.
        const markdown = $convertToMarkdownString(MARKDOWN_TRANSFORMERS, undefined, true);
        onChange(normalizeMarkdownOutput(markdown));
      });
    },
    [onChange],
  );

  return (
    <>
      <RichTextPlugin
        contentEditable={
          <ContentEditable
            className={classes.contentEditable}
            onFocus={onFocusEditor}
            onBlur={onBlurEditor}
          />
        }
        placeholder={<div className={classes.placeholder}>{placeholder}</div>}
        ErrorBoundary={LexicalErrorBoundary}
      />
      <HistoryPlugin />
      <ListPlugin />
      <CheckListPlugin />
      <TablePlugin />
      <HorizontalRulePlugin />
      <TabIndentationPlugin />
      <LinkPlugin />
      <AutoLinkPlugin matchers={AUTO_LINK_MATCHERS} />
      <MarkdownShortcutPlugin transformers={MARKDOWN_TRANSFORMERS} />
      <OnChangePlugin onChange={handleChange} ignoreSelectionChange={true} />
      <MarkdownSyncPlugin committedValue={committedValue} readOnly={readOnly ?? false} />
      <ReadOnlyPlugin readOnly={readOnly ?? false} />
      {!readOnly && anchorElem && (
        <>
          <FloatingTextFormatToolbarPlugin anchorElem={anchorElem} />
          <FloatingLinkEditorPlugin anchorElem={anchorElem} />
          <DraggableBlockPlugin anchorElem={anchorElem} />
          <SlashCommandPlugin anchorElem={anchorElem} aiSlash={aiSlash} />
          <TableActionMenuPlugin anchorElem={anchorElem} />
          <TableHoverActionsPlugin anchorElem={anchorElem} />
        </>
      )}
    </>
  );
}

/**
 * Tracks whether a scroll container has content below the fold and exposes a
 * "scroll down a page" action. Powers the read-mode "More ⌄" cue on long docs.
 * Re-measures on scroll, resize, content change, and a few post-mount ticks
 * (content height settles after fonts load and late markdown sync).
 */
function useOverflowMore(
  scrollRef: React.RefObject<HTMLElement>,
  enabled: boolean,
  watch: unknown,
) {
  const [hasMore, setHasMore] = React.useState(false);

  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !enabled) {
      setHasMore(false);
      return;
    }
    setHasMore(el.scrollHeight - el.clientHeight - el.scrollTop > 8);
  }, [scrollRef, enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    const el = scrollRef.current;
    if (!el) return undefined;
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    const timers = [120, 400, 900].map((t) => window.setTimeout(measure, t));
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    return () => {
      el.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      timers.forEach(clearTimeout);
      ro?.disconnect();
    };
    // `watch` re-runs measurement when the document content changes.
  }, [scrollRef, enabled, measure, watch]);

  const scrollMore = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({
      top: Math.min(el.scrollTop + el.clientHeight * 0.82, el.scrollHeight - el.clientHeight),
      behavior: 'smooth',
    });
  }, [scrollRef]);

  return { hasMore, scrollMore };
}

export default function LexicalMarkdownEditor(props: MarkdownEditorProps) {
  const {
    placeholder = 'Add a description...',
    committedValue,
    readOnly = false,
    onFocusEditor,
    onBlurEditor,
    onChange,
    barLabel,
    barAction,
    chromeless = false,
    aiSlash,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [anchorElem, setAnchorElem] = React.useState<HTMLElement | null>(null);

  useEffect(() => {
    if (containerRef.current) {
      setAnchorElem(containerRef.current);
    }
  }, []);

  // "More" cue is read-mode only — in edit mode the pinned action bar is the
  // affordance and a bottom fade would obscure the caret on the last line.
  const { hasMore, scrollMore } = useOverflowMore(innerRef, readOnly, committedValue?.value);

  const initialConfig = {
    namespace: 'LexicalMarkdownEditor',
    nodes: EDITOR_NODES,
    onError(error: Error) {
      console.error('[LexicalMarkdownEditor]', error);
    },
    theme: editorTheme,
    editable: !readOnly,
  };

  // The bar is present whenever there's something to show: format controls in
  // edit mode, or a host-provided label / action in read mode.
  const showBar = !readOnly || barLabel != null || barAction != null;

  const rootClasses = [
    classes.editorRoot,
    readOnly ? classes.readOnly : classes.editing,
    chromeless ? classes.chromeless : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div data-testid="lexical-markdown-editor" className={rootClasses} ref={containerRef}>
      <LexicalComposer initialConfig={initialConfig}>
        {/* One persistent bar: format controls (edit) or label (read) on the
            left, host action (e.g. Generate) pinned right. Bar chrome is owned
            here so reading and editing share a single composed surface. */}
        {showBar && (
          <div className={classes.bar} onClick={(e) => e.stopPropagation()}>
            <div className={classes.barMain}>{readOnly ? barLabel : <ToolbarPlugin />}</div>
            {barAction != null && <div className={classes.barAction}>{barAction}</div>}
          </div>
        )}
        <div className={classes.editorInner} ref={innerRef}>
          <BackgroundPlugins
            committedValue={committedValue}
            readOnly={readOnly}
            onChange={onChange}
            onFocusEditor={onFocusEditor}
            onBlurEditor={onBlurEditor}
            placeholder={placeholder}
            anchorElem={anchorElem}
            aiSlash={aiSlash}
          />
        </div>
        {readOnly && hasMore && (
          <>
            <div className={classes.scrollFade} aria-hidden="true" />
            <button
              type="button"
              className={classes.morePill}
              onClick={(e) => {
                e.stopPropagation();
                scrollMore();
              }}
            >
              <KeyboardArrowDown fontSize="small" />
              More
            </button>
          </>
        )}
      </LexicalComposer>
    </div>
  );
}
